import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  Box, Typography, Paper, Button, TextField, Alert, CircularProgress,
  IconButton, List, ListItem, ListItemText, InputAdornment, Chip, Divider
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';

export default function AdminSchools({ embedded = false }) {
  // Inside the Schools page tabs the page around it supplies the Layout.
  const Wrap = embedded ? React.Fragment : Layout;
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newSchool, setNewSchool] = useState('');
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'schools'));
        if (snap.exists()) setSchools(snap.data().list || []);
      } catch (e) {
        logError('AdminSchools', e.message || String(e), e.stack || '');
        setError('Failed to load schools.');
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleAdd = () => {
    const trimmed = newSchool.trim();
    if (!trimmed) return;
    if (schools.includes(trimmed)) {
      setError(`"${trimmed}" is already in the list.`);
      return;
    }
    const updated = [...schools, trimmed].sort((a, b) => a.localeCompare(b));
    setSchools(updated);
    setNewSchool('');
    setDirty(true);
    setError('');
  };

  const handleDelete = (school) => {
    setSchools(prev => prev.filter(s => s !== school));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(db, 'settings', 'schools'), { list: schools }, { merge: false });
      setSuccess(`Saved ${schools.length} schools.`);
      setDirty(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      logError('AdminSchools', e.message || String(e), e.stack || '');
      setError('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const filtered = schools.filter(s =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <Wrap><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></Wrap>
  );

  return (
    <Wrap>
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SchoolIcon sx={{ color: '#1E3A5F', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
              Schools
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              {schools.length} schools · shown in registration form autocomplete
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || !dirty}
          sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>

      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}

      {dirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click <strong>Save Changes</strong> to apply them.
        </Alert>
      )}

      {/* Add new school */}
      <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Add School</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
            fullWidth
            size="small"
            placeholder="School name..."
            value={newSchool}
            onChange={e => setNewSchool(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!newSchool.trim()}
            sx={{ whiteSpace: 'nowrap', backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
          >
            Add
          </Button>
        </Box>
      </Paper>

      {/* Search + list */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <TextField sx={{ backgroundColor: '#eeeeee', borderRadius: 1 }}
            fullWidth
            size="small"
            placeholder="Search schools..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
            }}
          />
          {search && (
            <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
              Showing {filtered.length} of {schools.length} schools
            </Typography>
          )}
        </Box>

        <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography sx={{ p: 3, color: '#aaa', textAlign: 'center' }}>
              {search ? 'No schools match your search.' : 'No schools in list.'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {filtered.map((school, i) => (
                <React.Fragment key={school}>
                  <ListItem
                    secondaryAction={
                      <IconButton size="small" color="error" onClick={() => handleDelete(school)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}
                  >
                    <ListItemText
                      primary={school}
                      sx={{ '& .MuiListItemText-primary': { fontSize: '0.9rem' } }}
                    />
                  </ListItem>
                  {i < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
    </Wrap>
  );
}
